import React, { useEffect } from 'react'
import {
  useHideModal,
  useIsShowModal,
  useSetActiveModal,
} from 'src/Modal/ModalStateContext'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import { LIST_ITEM_COPY } from 'src/components/Reusable/ListItem/ListItem.constants'
import { PolicyInfo } from 'src/components/Reusable/ListItem/ListItem.components'
import { ModalForm, ModalFormId } from '../../..'
import { useFrameworkWizard } from '../../../../../context/FormContext'
import { ReusablePoliciesModal } from '../Policies/ReusablePoliciesModal'
import { NoRecommendedTemplates } from '../NoRecommendedTemplates'
import { MultiSelectForm } from '../../../../Reusable/Forms/MultiSelectForm'
import { Spinner } from '../../../../../Throbber/Spinner'
import { useDepartmentForComplianceFramework } from '../../../../async/complianceframework/useDepartmentForComplianceFramework'
import { usePolicyForComplianceFramework } from '../../../../async/complianceframework/usePolicyForComplianceFramework'
import {
  SelectControlsRowItem,
  SelectControlsRowItemBaseProps,
} from '../../../../Reusable/Forms/MultiSelectFromComponents/SelectControlsRowItem'
import { DepartmentChipWithDepartment } from '../../../../../pages/Controls/DepartmentChip'

interface SelectControlsForComplianceFrameworkProps
  extends SelectControlsRowItemBaseProps {
  complianceFrameworkId: string
}

const ComplianceFrameworkPolicyInfo = ({
  policyModelId,
  complianceFrameworkId,
}: {
  policyModelId: string
  complianceFrameworkId: string
}) => {
  const { data, isLoading } = usePolicyForComplianceFramework(
    policyModelId,
    complianceFrameworkId,
  )
  const policyName = data?.getName() || LIST_ITEM_COPY.NO_POLICY
  const isWarning = !data && !isLoading
  return isLoading ? (
    <Spinner color="primary" size="m" />
  ) : (
    <PolicyInfo policyName={policyName} isWarning={isWarning} />
  )
}

const SelectControlsForComplianceFramework = ({
  control,
  setSelectedControlIds,
  selectedControlIds,
  complianceFrameworkId,
}: SelectControlsForComplianceFrameworkProps) => {
  const policyModelId = control.getPolicyId()
  const department = useDepartmentForComplianceFramework(
    control.getDepartmentId(),
    complianceFrameworkId,
  )

  return (
    <SelectControlsRowItem
      {...{ control, setSelectedControlIds, selectedControlIds }}
      customPolicyElement={
        <ComplianceFrameworkPolicyInfo
          policyModelId={policyModelId}
          complianceFrameworkId={complianceFrameworkId}
        />
      }
      customDepartmentElement={
        <DepartmentChipWithDepartment department={department} />
      }
      objectiveAsId
    />
  )
}

export const SelectControlsModal = (): JSX.Element => {
  const {
    controlsState: {
      suggestedControls,
      selectedControls,
      setSelectedControls,
      controlsInAccount,
    },
    frameworkState: { framework },
    loadingState: { loading },
  } = useFrameworkWizard()

  const hide = useHideModal({ modalId: ModalFormId.SELECT_CONTROLS })
  const show = useIsShowModal(ModalFormId.SELECT_CONTROLS)

  useEffect(() => {
    setSelectedControls(suggestedControls.map((elem) => elem.getObjective()))
  }, [setSelectedControls, suggestedControls])

  const handleDeselectAll = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setSelectedControls([])
  }
  const handleSelectAll = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    setSelectedControls(suggestedControls.map((elem) => elem.getObjective()))
  }

  const multiSelectFormProps = {
    formId: ModalFormId.SELECT_CONTROLS,
    title:
      'We recommend adding all of these controls. You can always change this later.',
    loading,
    handleSelectAll,
    handleDeselectAll,
    gridTemplateColumnStyling:
      'min-content minmax(min-content, auto) minmax(min-content, auto) minmax(min-content, 1fr)',
    gridItems: suggestedControls.length ? (
      <>
        {suggestedControls.map((control) => (
          <SelectControlsForComplianceFramework
            key={control.getId()}
            control={control}
            setSelectedControlIds={setSelectedControls}
            selectedControlIds={selectedControls}
            complianceFrameworkId={framework?.getModelId() || ''}
          />
        ))}
      </>
    ) : (
      <NoRecommendedTemplates templateType={'controls'} />
    ),
  }

  return (
    <div>
      <ReusablePoliciesModal />
      <ModalForm
        show={show}
        hide={hide}
        formId={ModalFormId.SELECT_CONTROLS}
        size="xl"
        title={`Add Controls for ${framework?.getName() || ''}`}
        onBack={useSetActiveModal(
          controlsInAccount.length
            ? ModalFormId.REUSABLE_CONTROLS
            : ModalFormId.CHOOSE_COMPLIANCE_FRAMEWORK,
        )}
        customSubmitButton={
          <StandardOpenModalButton modalId={ModalFormId.REUSABLE_POLICIES} />
        }
      >
        <MultiSelectForm showSelectButtons {...multiSelectFormProps} />
      </ModalForm>
    </div>
  )
}
